import type { PlayerSettings } from '@shefira/shared';

import {
  getMusicVolume,
  getSfxVolume,
  isMuted,
  onMutedChange,
  onVolumeChange,
  setMusicVolume,
  setMuted,
  setSfxVolume,
} from '../audio/sound.js';
import { language, onLanguageChosen, setLanguage } from '../i18n/language.js';
import { fetchSettings, pushSettings } from '../net/settings.js';
import { allBindings, onBindingsChange, replaceBindings } from './keybindings.js';
import { allPreferences, onPreferenceChange, replacePreferences } from './preferences.js';

/**
 * Keeps the settings in step with the account (ADR 0029). `localStorage`
 * stays the cache this browser starts from — the title screen sounds before
 * any session exists — and the account is the truth once one does:
 *
 * - at start, what the account saved replaces the local set; an account that
 *   never saved gets this browser's set, so a first visit loses nothing;
 * - afterwards every change, whichever window made it, is sent whole after a
 *   short pause, so a slider dragged across sends once.
 *
 * A server that cannot be reached is left alone: the settings still work
 * here, and the next visit tries again.
 */

/** A slider sends its resting value, not every step. */
const PUSH_DELAY_MS = 800;

let applying = false;
let timer: number | undefined;

/** Everything the account keeps, from the three stores. */
export function snapshotSettings(): PlayerSettings {
  return {
    muted: isMuted(),
    sfxVolume: getSfxVolume(),
    musicVolume: getMusicVolume(),
    keys: { ...allBindings() },
    ...allPreferences(),
    language: language(),
  };
}

/** The account's set into the stores, without sending it straight back. */
export function applySettings(settings: PlayerSettings): void {
  applying = true;
  try {
    if (settings.muted !== undefined) setMuted(settings.muted);
    if (settings.sfxVolume !== undefined) setSfxVolume(settings.sfxVolume);
    if (settings.musicVolume !== undefined) setMusicVolume(settings.musicVolume);
    replaceBindings(settings.keys ?? {});
    replacePreferences({
      ...(settings.damageNumbers === undefined ? {} : { damageNumbers: settings.damageNumbers }),
      ...(settings.combatFrame === undefined ? {} : { combatFrame: settings.combatFrame }),
      ...(settings.minimap === undefined ? {} : { minimap: settings.minimap }),
      ...(settings.vibration === undefined ? {} : { vibration: settings.vibration }),
      ...(settings.ping === undefined ? {} : { ping: settings.ping }),
      ...(settings.screenShake === undefined ? {} : { screenShake: settings.screenShake }),
      ...(settings.lowHealthAlert === undefined ? {} : { lowHealthAlert: settings.lowHealthAlert }),
      ...(settings.chat === undefined ? {} : { chat: settings.chat }),
      ...(settings.nightAndWeather === undefined
        ? {}
        : { nightAndWeather: settings.nightAndWeather }),
    });
    // Another language on the account: the page reloads into it (ADR 0069).
    // It came from the account: nothing to save back before reloading.
    if (settings.language !== undefined) setLanguage(settings.language, { save: false });
  } finally {
    applying = false;
  }
}

export function installSettingsSync(token: string): void {
  const schedule = (): void => {
    if (applying) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      pushSettings(token, snapshotSettings()).catch(() => {
        /* offline or expired: the settings still hold here */
      });
    }, PUSH_DELAY_MS);
  };
  void fetchSettings(token)
    .then(async (saved) => {
      if (saved === null) await pushSettings(token, snapshotSettings());
      else applySettings(saved);
      onMutedChange(schedule);
      onVolumeChange(schedule);
      onBindingsChange(schedule);
      onPreferenceChange(schedule);
      // A language change reloads the page at once: saved first, not after the pause.
      onLanguageChosen(async () => {
        window.clearTimeout(timer);
        await pushSettings(token, snapshotSettings());
      });
    })
    .catch(() => {
      /* unreachable: nothing is sent, so nothing on the account is overwritten */
    });
}
